'use client';

import { useEffect, useState } from 'react';
import { reportClientError } from '@/lib/client-errors';
import { logger } from '@/lib/logger';
import { isStaleBuildError, reloadIfStaleBuild } from '@/lib/stale-build';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // A tab older than the last deploy: reload onto the current build rather than show an error.
  const stale = isStaleBuildError(error);
  const [reloading, setReloading] = useState(stale);
  useEffect(() => {
    if (stale) setReloading(reloadIfStaleBuild(error));
  }, [error, stale]);

  useEffect(() => {
    logger.error('Application error:', error);
    // To the host's log pipeline as well — the console is not monitored.
    void reportClientError(error, { source: 'error-page', component: error.digest ? `digest:${error.digest}` : undefined });
  }, [error]);

  if (reloading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-sm text-gray-600 dark:text-gray-400">A new version of UZO is available. Reloading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <span className="text-6xl font-bold text-red-300 dark:text-red-600">!</span>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Something went wrong
        </h1>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {stale ? 'This tab is on an older version of UZO. Reload the page to continue.' : 'An unexpected error occurred. Please try again.'}
        </p>

        <button
          onClick={() => (stale ? window.location.reload() : reset())}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {stale ? 'Reload' : 'Try Again'}
        </button>
      </div>
    </div>
  );
}
